import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../../Context/Can';
import { Alert, Box, Button, CircularProgress, IconButton, LinearProgress, Paper, Snackbar, Typography } from '@mui/material';
import { FilterList } from '@mui/icons-material';
import dayjs from 'dayjs';
import { getMonthSiteVisits, getPendingDateRequests, getProjectSchedule, getProjectTimelines } from '../../../Redux/actions';
import { updateParams } from '../../../utils';
import { THEME_COLOR, buttonStyles } from '../../../theme/formStyles';
import ProjectStepper from '../../ProjectManagement/timeline/ProjectStepper';
import ProjectOverviewCards from '../../ProjectManagement/timeline/ProjectOverviewCards';
import ProjectEditorDialog from '../../ProjectManagement/editor/ProjectEditorDialog';
import ProjectSchedulePanel from '../../ProjectManagement/gantt/ProjectSchedulePanel';
import AllProjectsGantt from '../../ProjectManagement/gantt/AllProjectsGantt';
import TicketsDialog from '../../ProjectManagement/tickets/TicketsDialog';
import TicketVisitDialog from '../../ProjectManagement/tickets/TicketVisitDialog';
import PortfolioKpis from '../../ProjectManagement/portfolio/PortfolioKpis';
import { matchesFilters, pmFilterOptions } from '../../ProjectManagement/portfolio/portfolio.utils';
import Filter from '../../Common/Filter';
import ScheduleToolbar, { SCHEDULE_VIEWS } from '../../ProjectManagement/schedule/ScheduleToolbar';
import OngoingTasksStrip from '../../ProjectManagement/schedule/OngoingTasksStrip';
import DayListView from '../../ProjectManagement/schedule/DayListView';
import TaskMonthGrid from '../../ProjectManagement/schedule/TaskMonthGrid';
import TeamBoard from '../../ProjectManagement/schedule/TeamBoard';
import SubTaskDetailDialog from '../../ProjectManagement/schedule/SubTaskDetailDialog';
import DayTasksDialog from '../../ProjectManagement/schedule/DayTasksDialog';
import DateRequestsBox from '../../ProjectManagement/schedule/DateRequests';
import { UNASSIGNED, engineerKey, engineerName, isTicketItem, itemKey } from '../../ProjectManagement/projectSchedule.utils';

// Project timeline + site schedule (internal team). Admins create and edit projects here with the project
// editor, which saves to the same tables as Settings › Project Management.
export default function ProjectTimeline() {
    const dispatch = useDispatch();
    const currentUserId = useSelector(st => st.currentUser?.uniqueId);
    const ability = useAbility(AbilityContext);
    const canEditProjects = ability.can('manage', 'EditProjectManagement');
    const navigate = useNavigate();
    const [queryParams] = useSearchParams();
    const existingParams = Object.fromEntries([...queryParams]);

    const projectId = existingParams.project && existingParams.project !== 'all' ? Number(existingParams.project) : 'all';
    const view = SCHEDULE_VIEWS.some(v => v.value === existingParams.view) ? existingParams.view : 'days';
    const engineer = existingParams.engineer || 'all';
    // Portfolio filters (Location / Facility / Project Owner), set with Common/Filter.js and kept in the URL
    const portfolioFilters = useMemo(
        () => ({ location: existingParams.location || '', facility: existingParams.facility || '', projectOwner: existingParams.projectOwner || '' }),
        [existingParams.location, existingParams.facility, existingParams.projectOwner]
    );
    // Filter.js treats every URL parameter it is given as a filter (its "Clear" empties them all), so it only
    // gets these three: the view, engineer and project parameters stay as they are
    const filterQueryParams = useMemo(
        () => new URLSearchParams(Object.entries(portfolioFilters).filter(([, v]) => v)),
        [portfolioFilters]
    );

    const [timelines, setTimelines] = useState([]);
    const [timelinesState, setTimelinesState] = useState({ loading: true, error: null });
    const [schedule, setSchedule] = useState([]);
    const [visits, setVisits] = useState([]);
    const [scheduleState, setScheduleState] = useState({ loading: true, error: null });
    const [cursor, setCursor] = useState({ year: dayjs().year(), month: dayjs().month() });
    const [openTaskRef, setOpenTaskRef] = useState(null);
    const [openDay, setOpenDay] = useState(null);
    const [editing, setEditing] = useState(null); // { projectId } — null projectId = new project
    const [ticketsFor, setTicketsFor] = useState(null); // { project, focusId }
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [planReload, setPlanReload] = useState(0);
    const [notice, setNotice] = useState('');
    const [dateRequests, setDateRequests] = useState([]);
    const scheduleRequest = useRef(0);

    const updateQueryParams = (params) => {
        navigate({ search: updateParams(existingParams, params).toString() }, { replace: true });
    };

    // silent: refresh in place (after a status change or timeline save) without the loading spinner
    const fetchTimelines = useCallback(({ silent = false } = {}) => {
        if (!silent) setTimelinesState({ loading: true, error: null });
        dispatch(getProjectTimelines({})).then((res) => {
            if (res?.data?.success) {
                setTimelines(res.data.data || []);
                setTimelinesState({ loading: false, error: null });
            } else {
                setTimelinesState({ loading: false, error: res?.data?.message || 'Could not load projects' });
            }
        }).catch(() => setTimelinesState({ loading: false, error: 'Could not load projects' }));
    }, [dispatch]);

    const fetchSchedule = useCallback(() => {
        // Ignore responses from older requests when the month or project changes quickly
        const requestNo = ++scheduleRequest.current;
        setScheduleState(s => ({ ...s, loading: true, error: null }));
        const params = { month: cursor.month + 1, year: cursor.year };
        if (projectId !== 'all') params.project_id = projectId;
        Promise.all([dispatch(getProjectSchedule(params)), dispatch(getMonthSiteVisits(params))]).then(([res, visitsRes]) => {
            if (requestNo !== scheduleRequest.current) return;
            // Visits only add times and hours; if they fail the schedule still shows
            setVisits(visitsRes?.data?.success ? visitsRes.data.data || [] : []);
            if (res?.data?.success) {
                setSchedule(res.data.data || []);
                setScheduleState({ loading: false, error: null });
            } else {
                setScheduleState({ loading: false, error: res?.data?.message || 'Could not load the schedule' });
            }
        }).catch(() => {
            if (requestNo === scheduleRequest.current) setScheduleState({ loading: false, error: 'Could not load the schedule' });
        });
    }, [dispatch, cursor, projectId]);

    useEffect(() => { fetchTimelines(); }, [fetchTimelines]);
    useEffect(() => { fetchSchedule(); }, [fetchSchedule]);

    // Client date change requests waiting for the team; failures just hide the box
    const fetchDateRequests = useCallback(() => {
        dispatch(getPendingDateRequests()).then((res) => setDateRequests(res?.data?.success ? res.data.data || [] : []));
    }, [dispatch]);
    useEffect(() => { fetchDateRequests(); }, [fetchDateRequests]);

    // A status change, check-in or approved date change can move progress, the current step and the calendar
    const refreshAll = () => {
        fetchSchedule();
        fetchTimelines({ silent: true });
        fetchDateRequests();
        setPlanReload(k => k + 1);
    };

    // Keep the open dialog in step with refreshed data (status changes to In Progress on check-in).
    // A bare {key} (opened from a date request or the Gantt, maybe in another month) waits until that month's schedule loads.
    const openTask = openTaskRef ? schedule.find(t => itemKey(t) === openTaskRef.key) || (openTaskRef.sub_task_name ? openTaskRef : null) : null;
    const setOpenTask = (task) => setOpenTaskRef(task ? { ...task, key: itemKey(task) } : null);

    const selectedProject = projectId === 'all' ? null : timelines.find(p => p.id === projectId) || null;

    const engineers = useMemo(() => {
        const map = new Map();
        schedule.forEach(t => {
            const key = engineerKey(t);
            if (!map.has(key)) map.set(key, { key, name: engineerName(t), count: 0 });
            map.get(key).count++;
        });
        const list = Array.from(map.values()).sort((a, b) => (a.key === UNASSIGNED) - (b.key === UNASSIGNED) || a.name.localeCompare(b.name));
        // Keep a filter chosen in another month selectable even when it has no work in this one
        if (engineer !== 'all' && !map.has(engineer)) list.push({ key: engineer, name: engineer === UNASSIGNED ? 'Unassigned' : engineer, count: 0 });
        return list;
    }, [schedule, engineer]);

    const filteredTimelines = useMemo(() => timelines.filter(p => matchesFilters(p, portfolioFilters)), [timelines, portfolioFilters]);
    const filterChoices = useMemo(() => pmFilterOptions(timelines), [timelines]);
    const activeFilterCount = Object.values(portfolioFilters).filter(Boolean).length;

    const visibleTasks = useMemo(
        () => (engineer === 'all' ? schedule : schedule.filter(t => engineerKey(t) === engineer)),
        [schedule, engineer]
    );

    const selectProject = (id) => {
        updateQueryParams({ project: id === 'all' ? '' : String(id) });
    };

    const jumpToStep = (step) => {
        if (!step.start_date) return;
        const d = dayjs(step.start_date);
        setCursor({ year: d.year(), month: d.month() });
    };

    const shiftMonth = (delta) => {
        const d = dayjs().year(cursor.year).month(cursor.month).date(1).add(delta, 'month');
        setCursor({ year: d.year(), month: d.month() });
    };

    const siteNames = useMemo(() => Object.fromEntries(timelines.map(p => [p.facility_id, p.facility_name])), [timelines]);
    const visibleRequests = projectId === 'all' ? dateRequests : dateRequests.filter(r => r.project_id === projectId);
    const openRequestTask = (request) => {
        const d = dayjs(request.original_start_date || undefined);
        setCursor({ year: d.year(), month: d.month() });
        setOpenTaskRef({ key: `project:${request.sub_task_id}` });
    };
    // A sub-task clicked on the Gantt: load its month, then open it
    const openGanttSubTask = (st) => {
        if (st.start_date) { const d = dayjs(st.start_date); setCursor({ year: d.year(), month: d.month() }); }
        setOpenTaskRef({ key: `project:${st.id}` });
    };
    const openTickets = (project, focusId = null) => setTicketsFor({ project, focusId });
    const onRequestAnswered = (message) => { setNotice(message); refreshAll(); };

    const monthLabel = dayjs().year(cursor.year).month(cursor.month).format('MMMM YYYY');

    return (
        <Box sx={{ p: { xs: 0, md: 2 } }}>
            {/* ── Timeline ───────────────────────────── */}
            <Paper sx={{ borderRadius: 3, boxShadow: 'none', border: '1px solid #eaeaea', p: { xs: 1.5, md: 2 }, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: 1.75 }}>
                    <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '15px' }}>Project timeline</Typography>
                        <Typography sx={{ fontSize: '12.5px', color: '#6b7785' }}>
                            {selectedProject ? `${selectedProject.facility_name}${selectedProject.location_name ? ` · ${selectedProject.location_name}` : ''}` : activeFilterCount ? `${filteredTimelines.length} of ${timelines.length} projects` : `All ${timelines.length} projects`}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
                        {selectedProject && (
                            <>
                                <Button variant="outlined" onClick={() => openTickets(selectedProject)} sx={{ ...buttonStyles.outlined, ...(selectedProject.open_tickets ? { borderColor: '#f3c2c2', color: '#c62828' } : {}) }}>
                                    🎫 Tickets{selectedProject.open_tickets ? ` · ${selectedProject.open_tickets} open` : ''}
                                </Button>
                                {canEditProjects && (
                                    <Button variant="outlined" onClick={() => setEditing({ projectId: selectedProject.id })} sx={buttonStyles.outlined}>✏️ Edit project</Button>
                                )}
                                <Button onClick={() => selectProject('all')} sx={{ color: THEME_COLOR, textTransform: 'none' }}>← All projects</Button>
                            </>
                        )}
                        {canEditProjects && (
                            <Button variant="contained" onClick={() => setEditing({ projectId: null })} sx={buttonStyles.contained}>＋ New project</Button>
                        )}
                        {projectId === 'all' && (
                            <IconButton onClick={() => setFiltersOpen(true)} title="Apply Filter">
                                <FilterList />
                            </IconButton>
                        )}
                    </Box>
                </Box>

                {timelinesState.loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress sx={{ color: THEME_COLOR }} /></Box>
                ) : timelinesState.error ? (
                    <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => fetchTimelines()}>Retry</Button>}>{timelinesState.error}</Alert>
                ) : selectedProject ? (
                    <ProjectStepper project={selectedProject} onStepClick={jumpToStep} />
                ) : projectId !== 'all' ? (
                    <Alert severity="warning">This project no longer exists. <Button size="small" onClick={() => selectProject('all')}>Show all projects</Button></Alert>
                ) : (
                    <>
                        <Filter
                            filterArea="ProjectManagement"
                            showFilterDialog={filtersOpen}
                            setShowFilterDialog={setFiltersOpen}
                            updateQueryParams={updateQueryParams}
                            queryParams={filterQueryParams}
                            setIsSearchOrFilterApplied={() => {}}
                            pmFilterOptions={filterChoices}
                        />
                        <PortfolioKpis projects={filteredTimelines} />
                        <ProjectOverviewCards projects={filteredTimelines} schedule={schedule} onSelect={selectProject} />
                    </>
                )}
            </Paper>

            {/* ── Schedule ───────────────────────────── */}
            <Paper sx={{ borderRadius: 3, boxShadow: 'none', border: '1px solid #eaeaea', px: { xs: 1.5, md: 2 }, pb: 2, position: 'relative' }}>
                <ScheduleToolbar
                    label={monthLabel}
                    view={view}
                    onViewChange={(v) => updateQueryParams({ view: v === 'days' ? '' : v })}
                    onPrev={() => shiftMonth(-1)}
                    onNext={() => shiftMonth(1)}
                    onToday={() => setCursor({ year: dayjs().year(), month: dayjs().month() })}
                    engineers={engineers}
                    engineer={engineer}
                    onEngineerChange={(v) => updateQueryParams({ engineer: v === 'all' ? '' : v })}
                />
                <DateRequestsBox requests={visibleRequests} siteNames={siteNames} canAnswer={canEditProjects} onOpenTask={openRequestTask} onDone={onRequestAnswered} />
                {view !== 'gantt' && scheduleState.loading && <LinearProgress sx={{ height: 2, bgcolor: '#e6f4f2', '& .MuiLinearProgress-bar': { bgcolor: THEME_COLOR } }} />}

                {view === 'gantt' ? (
                    selectedProject
                        ? <ProjectSchedulePanel projectId={selectedProject.id} reloadKey={planReload} onMessage={setNotice}
                            onOpenSubTask={openGanttSubTask} onOpenTicket={(t) => openTickets(selectedProject, t.id)} />
                        : <AllProjectsGantt projects={filteredTimelines} onSelect={selectProject} />
                ) : scheduleState.error ? (
                    <Alert severity="error" sx={{ mt: 1.5 }} action={<Button color="inherit" size="small" onClick={fetchSchedule}>Retry</Button>}>{scheduleState.error}</Alert>
                ) : (
                    <Box sx={{ opacity: scheduleState.loading ? 0.6 : 1, transition: 'opacity .15s' }}>
                        <OngoingTasksStrip tasks={visibleTasks} year={cursor.year} month={cursor.month} onOpenTask={setOpenTask} />
                        <Box sx={{ pt: view === 'days' ? 0 : 1.5 }}>
                            {view === 'month' && <TaskMonthGrid tasks={visibleTasks} year={cursor.year} month={cursor.month} onOpenTask={setOpenTask} onOpenDay={setOpenDay} />}
                            {view === 'team' && <TeamBoard tasks={visibleTasks} visits={visits} year={cursor.year} month={cursor.month} onOpenDay={setOpenDay} />}
                            {view === 'days' && <DayListView tasks={visibleTasks} visits={visits} year={cursor.year} month={cursor.month} onOpenTask={setOpenTask} />}
                        </Box>
                    </Box>
                )}
            </Paper>

            <DayTasksDialog ymd={openDay} tasks={visibleTasks} visits={visits} onClose={() => setOpenDay(null)} onOpenTask={setOpenTask} />
            {openTask && isTicketItem(openTask) ? (
                <TicketVisitDialog item={openTask} currentUserId={currentUserId} onClose={() => setOpenTask(null)} onChanged={refreshAll}
                    onOpenTickets={(item) => openTickets({ id: item.project_id, facility_id: item.facility_id, facility_name: item.facility_name }, item.ticket_id)} />
            ) : (
                <SubTaskDetailDialog task={openTask} currentUserId={currentUserId} onClose={() => setOpenTask(null)} onChanged={refreshAll}
                    dateRequest={openTask ? dateRequests.find(r => r.sub_task_id === openTask.id) : null} canAnswerRequests={canEditProjects} onRequestAnswered={onRequestAnswered}
                    onEditProject={canEditProjects ? (id) => setEditing({ projectId: id }) : undefined} />
            )}
            {ticketsFor && (
                <TicketsDialog project={ticketsFor.project} focusTicketId={ticketsFor.focusId} canManage={canEditProjects} currentUserId={currentUserId}
                    onClose={() => setTicketsFor(null)} onChanged={refreshAll} />
            )}
            {editing && (
                <ProjectEditorDialog
                    projectId={editing.projectId}
                    onClose={() => setEditing(null)}
                    onSaved={(id, message) => {
                        setEditing(null);
                        setNotice(message);
                        refreshAll();
                        if (id !== projectId) selectProject(id);
                    }}
                />
            )}
            <Snackbar open={!!notice} autoHideDuration={3000} onClose={() => setNotice('')} message={notice} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
        </Box>
    );
}
